/**
 * Canonical Routes Configuration
 * Maps clean /abcd slugs to internal screen names
 */

export interface RouteConfig {
  slug: string;
  screen: string;
  title: string;
  description: string;
  public: boolean;
}

export const ROUTES: RouteConfig[] = [
  // Main user flows
  { slug: 'home', screen: 'landing', title: 'CoreBids - Smart Roofing Solutions', description: 'Get accurate roofing estimates and connect with qualified contractors', public: true },
  // CB:BEGIN sign-in-canonical-route – so useUrlSync does not rewrite /sign-in to /
  { slug: 'sign-in', screen: 'sign-in', title: 'Sign In - CoreBids', description: 'Sign in to your account', public: true },
  // CB:END sign-in-canonical-route
  { slug: 'signup', screen: 'signup', title: 'Sign Up - Twilio SMS Opt-In', description: 'Sign up with SMS opt-in for Twilio verification', public: true },
  { slug: 'estimate', screen: 'estimate-form', title: 'Get Your Roofing Estimate', description: 'Complete our form to get a detailed roofing estimate', public: true },
  { slug: 'measure', screen: 'measurement-input', title: 'Provide Measurements', description: 'Help us create an accurate estimate with your measurements', public: false },
  { slug: 'dashboard', screen: 'customer-dashboard', title: 'Customer Dashboard', description: 'Manage your roofing projects and view progress', public: false },
  // CB:BEGIN homeowner-help-route
  { slug: 'dashboard/help', screen: 'homeowner-help', title: 'Homeowner Help Center', description: 'Step-by-step homeowner walkthrough and support guidance', public: false },
  { slug: 'help', screen: 'homeowner-help', title: 'Homeowner Help Center (legacy alias)', description: 'Legacy homeowner help alias', public: false },
  // CB:END homeowner-help-route
  { slug: 'documents', screen: 'documents', title: 'Documents', description: 'View signed contract, invoice, and accepted quote', public: false },

  // Project workflow
  { slug: 'wizard', screen: 'project-wizard', title: 'Project Setup Wizard', description: 'Step-by-step project configuration', public: false },
  { slug: 'reports', screen: 'roof-report-coming-soon', title: 'Roof Report Status', description: 'Track your roof report generation progress', public: false },
  { slug: 'next-steps', screen: 'roof-report-coming-soon', title: 'Next Steps', description: 'Your roofing project is being processed', public: false },
  { slug: 'bids', screen: 'contractor-bids', title: 'Contractor Bids', description: 'Review and compare contractor bids for your project', public: false },
  { slug: 'contractor-details', screen: 'contractor-details', title: 'Contractor Details', description: 'View detailed information about a contractor', public: false },
  { slug: 'payment', screen: 'payment-screen', title: 'Payment', description: 'Secure payment processing for your project', public: false },
  { slug: 'escrow-dashboard', screen: 'escrow-dashboard', title: 'Escrow & Milestones', description: 'Track project milestones and escrow payments', public: false },
  { slug: 'contractor-escrow-dashboard', screen: 'contractor-escrow-dashboard', title: 'Project Milestones', description: 'Track project milestones and payments', public: false },
  // CB:BEGIN route-alias-project-details
  { slug: 'project-details', screen: 'project-details', title: 'Project Details', description: 'Enter bid details and submit your proposal', public: false },
  // CB:END route-alias-project-details
  
  // Admin dashboard routes
  { slug: 'admin', screen: 'admin-dashboard', title: 'Admin Dashboard', description: 'Complete project and user management', public: false },
  // CB:BEGIN admin-create-route
  { slug: 'admin-create', screen: 'admin-create', title: 'Create Admin', description: 'Create or promote an admin account', public: true },
  // CB:END admin-create-route
  { slug: 'admin/homeowners', screen: 'admin-homeowners-list', title: 'Admin - Homeowners', description: 'Manage homeowners and projects', public: false },
  { slug: 'admin/homeowners/add', screen: 'admin-homeowner-add', title: 'Add Homeowner', description: 'Create new homeowner account', public: false },
  { slug: 'admin/contractors', screen: 'admin-contractors-list', title: 'Admin - Contractors', description: 'Manage contractors and verification', public: false },
  { slug: 'admin/verification-queue', screen: 'admin-verification-queue', title: 'Verification Queue', description: 'Review and approve contractor verification submissions', public: false },
  { slug: 'admin/contractors/add', screen: 'admin-contractor-add', title: 'Add Contractor', description: 'Create new contractor account', public: false },
  { slug: 'admin/projects', screen: 'admin-projects-list', title: 'Admin - Projects', description: 'Manage all projects', public: false },
  { slug: 'admin/projects/:id', screen: 'admin-project-cockpit', title: 'Project Cockpit', description: 'Complete project management interface', public: false },
  { slug: 'admin/settings', screen: 'admin-settings', title: 'Admin Settings', description: 'Platform configuration and feature flags', public: false },
  // CB:BEGIN admin-invoices-create-route
  { slug: 'admin/invoices/create', screen: 'admin-invoices-create', title: 'Create Invoice', description: 'Create invoice from quote or upload quote PDF', public: false },
  // CB:END admin-invoices-create-route
  // CB:BEGIN admin-finance-route
  { slug: 'admin/finance', screen: 'admin-finance', title: 'Admin Finance', description: 'Escrow, ledger, invoices and reconcile', public: false },
  // CB:END admin-finance-route
  // CB:BEGIN operations-control-plane-route
  { slug: 'admin/operations', screen: 'admin-operations', title: 'Admin Operations', description: 'Search and project timeline', public: false },
  // CB:END operations-control-plane-route
  // CB:BEGIN admin-ops-route
  { slug: 'admin/ops', screen: 'admin-ops', title: 'Admin Ops', description: 'Off-platform projects and overrides', public: false },
  // CB:END admin-ops-route
  // Contractor flows
  { slug: 'contractor', screen: 'contractor-landing', title: 'Contractor Portal', description: 'Access contractor tools and project management', public: true },
  // CB:BEGIN fr-014-realtor-routes
  { slug: 'realtor', screen: 'realtor-landing', title: 'Realtor Portal', description: 'Request quotes and share with your clients', public: true },
  { slug: 'realtor-dashboard', screen: 'realtor-dashboard', title: 'Realtor Dashboard', description: 'Manage quote requests and client portal links', public: false },
  { slug: 'realtor/quotes/:id', screen: 'realtor-quote-detail', title: 'Quote Detail', description: 'View bids and download PDF or invite client', public: false },
  { slug: 'realtor/portal/:token', screen: 'realtor-client-portal', title: 'Client Portal', description: 'View bids and accept one to start your project', public: true },
  // CB:END fr-014-realtor-routes
  // CB:BEGIN route-alias-contractor-dashboard
  { slug: 'contractor-dashboard', screen: 'contractor-dashboard', title: 'Contractor Dashboard', description: 'Manage your projects and bids', public: false },
  // CB:BEGIN contractor-help-route
  { slug: 'contractor/help', screen: 'contractor-help', title: 'Contractor Help Center', description: 'Interactive contractor guide for bidding and project execution', public: false },
  { slug: 'contractor-help', screen: 'contractor-help', title: 'Contractor Help Center (legacy alias)', description: 'Legacy contractor help alias', public: false },
  // CB:END contractor-help-route
  // CB:END route-alias-contractor-dashboard
  // CB:BEGIN contractor-signup-route
  { slug: 'contractor-start', screen: 'contractor-signup', title: 'Join as Contractor', description: 'Create your contractor account to start bidding', public: true },
  // CB:END contractor-signup-route
  // CB:BEGIN invite-route
  { slug: 'invite', screen: 'invite-preview', title: 'Project Invitation', description: 'View project details and submit your bid', public: true },
  // CB:END invite-route
  // CB:BEGIN route-alias-contractor-verification
  { slug: 'contractor-verification', screen: 'contractor-verification', title: 'Contractor Verification', description: 'Complete your contractor verification process', public: false },
  { slug: 'contractor/onboarding', screen: 'contractor-verification', title: 'Contractor Onboarding', description: 'Complete your contractor verification process', public: false },
  { slug: 'verify', screen: 'contractor-verification', title: 'Contractor Verification', description: 'Complete your contractor verification process', public: false },
  // CB:END route-alias-contractor-verification
  { slug: 'contractor-dash', screen: 'contractor-dashboard', title: 'Contractor Dashboard', description: 'Manage your projects and bids', public: false },
  { slug: 'current-bids', screen: 'current-bids', title: 'Current Bids', description: 'View and manage your current project bids', public: false },
  // FIX: Dedicated route for contractor project details by id
  { slug: 'contractor/projects', screen: 'contractor-project-details', title: 'Project Details', description: 'Enter bid details and submit your proposal', public: false },
  
  // Information pages
  { slug: 'AboutUs', screen: 'about', title: 'About CoreBids', description: 'Learn about CoreBids and how we connect homeowners with trusted contractors', public: true },
  { slug: 'faq', screen: 'faq', title: 'Frequently Asked Questions', description: 'Common questions about CoreBids services', public: true },
  { slug: 'accept-terms', screen: 'accept-terms', title: 'Accept Terms', description: 'Accept Terms and Privacy to continue', public: true },
  { slug: 'terms', screen: 'terms', title: 'Terms of Service', description: 'CoreBids terms and conditions', public: true },
  // CB:BEGIN payment-protection-terms-route
  { slug: 'payment-terms', screen: 'payment-terms', title: 'Payment Protection & Secure Payment Holding Terms', description: 'Project payment protection and no-escrow service terms', public: true },
  // CB:END payment-protection-terms-route
  // CB:BEGIN homeowner-project-agreement-route
  { slug: 'homeowner-agreement', screen: 'homeowner-agreement', title: 'Homeowner Project Agreement', description: 'Agreement governing bid acceptance and payment facilitation', public: true },
  // CB:END homeowner-project-agreement-route
  // CB:BEGIN contractor-bid-agreement-route
  { slug: 'contractor-agreement', screen: 'contractor-agreement', title: 'Contractor Bid Agreement', description: 'Agreement governing contractor bids and platform payment use', public: true },
  // CB:END contractor-bid-agreement-route
  { slug: 'financing', screen: 'enhancify-financing', title: 'Financing Options', description: 'View flexible project financing options powered by Enhancify', public: true },
  { slug: 'privacy', screen: 'privacy-policy', title: 'Privacy Policy', description: 'How we protect your information', public: true },
  { slug: 'accessibility', screen: 'accessibility-statement', title: 'Accessibility Statement', description: 'Our commitment to accessibility', public: true },
  
  // CB:BEGIN kpi-route
  // Public KPI dashboard for production at /kpi
  { slug: 'kpi', screen: 'data-tracking', title: 'CoreBids KPI Dashboard', description: 'Public KPIs and demo analytics', public: true },
  // CB:END kpi-route
  
  // Special routes
  { slug: 'waitlist', screen: 'waitlist', title: 'Join CoreBids Waitlist', description: 'Get early access to our roofing platform', public: true },
  { slug: 'inbox', screen: 'inbox', title: 'Messages', description: 'View your project communications', public: false },
  { slug: 'history', screen: 'project-history', title: 'Project History', description: 'View your completed projects', public: false },
  
  // CB:BEGIN stripe-demo-routes
  { slug: 'demo/stripe-connect', screen: 'stripe-connect-demo', title: 'Stripe Connect Demo', description: 'Test contractor and homeowner onboarding', public: true },
  { slug: 'demo/payments', screen: 'demo-payments', title: 'Demo Payments', description: 'Test milestone payments and escrow', public: true },
  // CB:END stripe-demo-routes
];

/**
 * Get route configuration by slug
 */
export function getRouteBySlug(slug: string): RouteConfig | undefined {
  return ROUTES.find(route => route.slug === slug);
}

/**
 * Get route configuration by screen name
 */
export function getRouteByScreen(screen: string): RouteConfig | undefined {
  return ROUTES.find(route => route.screen === screen);
}

/**
 * Get canonical URL for a screen
 */
export function getCanonicalUrl(screen: string): string {
  const route = getRouteByScreen(screen);
  if (!route) return '/';
  // CB:BEGIN canonical-root-landing
  // Special-case landing page to live at root path
  if (screen === 'landing') return '/';
  // CB:END canonical-root-landing

  // CB:BEGIN admin-quotes-new-canonical
  if (screen === 'admin-quotes-new') return '/admin/quotes/new';
  if (screen === 'admin-quotes-drafts') return '/admin/quotes/drafts';
  // CB:END admin-quotes-new-canonical
  // CB:BEGIN admin-invoices-create-canonical
  if (screen === 'admin-invoices-create') return '/admin/invoices/create';
  // CB:END admin-invoices-create-canonical
  if (screen === 'admin-finance') return '/admin/finance';
  if (screen === 'admin-operations') return '/admin/operations';
  if (screen === 'admin-ops') return '/admin/ops';

  // CB:BEGIN preserve-next-steps-url
  // Special-case: if we're on /next-steps and the screen is roof-report-coming-soon,
  // preserve the /next-steps URL instead of canonicalizing to /reports
  if (screen === 'roof-report-coming-soon' && window.location.pathname === '/next-steps') {
    return '/next-steps';
  }
  // CB:END preserve-next-steps-url

  // CB:BEGIN preserve-invite-token-url
  // CRITICAL: Preserve the full invite URL including token parameter
  if (screen === 'invite-preview') {
    const currentPath = window.location.pathname;
    // If we're already on an /invite/:token path, preserve it exactly
    if (currentPath.match(/^\/invite\/[^/]+/)) {
      return currentPath;
    }
  }
  // CB:END preserve-invite-token-url

  // CB:BEGIN preserve-dynamic-project-routes
  // FIX: Preserve dynamic project ID in URL for contractor project details
  if (screen === 'contractor-project-details') {
    const currentPath = window.location.pathname;
    // If we're already on a /contractor/projects/:id path, preserve it
    if (currentPath.match(/^\/contractor\/projects\/[^/]+/)) {
      return currentPath;
    }
    // Otherwise, try to get project ID from localStorage
    try {
      const projectId = localStorage.getItem('active_project_id');
      if (projectId && projectId !== 'demo-project' && projectId !== '0') {
        return `/contractor/projects/${projectId}`;
      }
    } catch {}
  }
  // CB:END preserve-dynamic-project-routes

  return `/${route.slug}`;
}

/**
 * Get screen name from canonical URL
 */
export function getScreenFromUrl(pathname: string): string | null {
  // CB:BEGIN normalize-url-before-route-parse
  const cleanPath = pathname.split('#')[0].split('?')[0];
  const normalizedPath = cleanPath.replace(/^\/+/, '').replace(/\/+$/, '');
  const slug = normalizedPath.split('/')[0];
  // CB:END normalize-url-before-route-parse
  if (!slug) return 'landing'; // Default to landing page for root URL

  // CB:BEGIN exact-static-slug-match
  // Support nested static slugs like /dashboard/help and /contractor/help
  const exactStaticRoute = ROUTES.find((route) => !route.slug.includes(':') && route.slug === normalizedPath);
  if (exactStaticRoute) return exactStaticRoute.screen;
  // CB:END exact-static-slug-match

  if (pathname === '/documents') return 'documents';

  // CB:BEGIN sign-in-route – full-page sign-in so email/password form works (modal can show sign-up first)
  if (pathname === '/sign-in' || pathname.startsWith('/sign-in?')) return 'sign-in';
  // CB:END sign-in-route

  // CB:BEGIN invite-route-handling
  // Handle /invite/:token route
  if (slug === 'invite' && pathname.match(/^\/invite\/[^/]+/)) {
    return 'invite-preview';
  }
  // CB:END invite-route-handling
  // CB:BEGIN fr-014-realtor-portal-route
  if (slug === 'realtor' && pathname.match(/^\/realtor\/portal\/[^/]+/)) {
    return 'realtor-client-portal';
  }
  if (slug === 'realtor' && pathname.match(/^\/realtor\/quotes\/[^/]+/)) {
    return 'realtor-quote-detail';
  }
  // CB:END fr-014-realtor-portal-route
  
  // FIX: Handle dynamic contractor project routes
  if (slug === 'contractor' && pathname.includes('/contractor/projects/')) {
    return 'contractor-project-details';
  }
  if (pathname === '/contractor/current-bids' || pathname.startsWith('/contractor/current-bids')) {
    return 'current-bids';
  }
  
  // CB:BEGIN admin-quotes-new-route
  if (pathname === '/admin/quotes/new' || pathname.startsWith('/admin/quotes/new')) {
    return 'admin-quotes-new';
  }
  if (pathname === '/admin/quotes/drafts' || pathname.startsWith('/admin/quotes/drafts')) {
    return 'admin-quotes-drafts';
  }
  // CB:END admin-quotes-new-route
  // CB:BEGIN admin-invoices-create-route
  if (pathname === '/admin/invoices/create' || pathname.startsWith('/admin/invoices/create')) {
    return 'admin-invoices-create';
  }
  // CB:END admin-invoices-create-route
  if (pathname === '/admin/finance' || pathname.startsWith('/admin/finance')) {
    return 'admin-finance';
  }
  // CB:BEGIN operations-control-plane-route
  if (pathname === '/admin/operations' || pathname.startsWith('/admin/operations')) {
    return 'admin-operations';
  }
  // CB:END operations-control-plane-route
  if (pathname === '/admin/ops' || pathname.startsWith('/admin/ops')) {
    return 'admin-ops';
  }
  // CB:BEGIN admin-subroutes-users-projects
  if (pathname === '/admin' || pathname === '/admin/') {
    return 'admin-dashboard';
  }
  if (pathname.match(/^\/admin\/projects\/[^/]+/)) {
    return 'admin-project-cockpit';
  }
  if (pathname === '/admin/projects' || pathname.startsWith('/admin/projects')) {
    return 'admin-projects-list';
  }
  if (pathname === '/admin/homeowners' || pathname.startsWith('/admin/homeowners')) {
    return 'admin-homeowners-list';
  }
  if (pathname === '/admin/contractors' || pathname.startsWith('/admin/contractors')) {
    return 'admin-contractors-list';
  }
  if (pathname === '/admin/verification-queue' || pathname.startsWith('/admin/verification-queue')) {
    return 'admin-verification-queue';
  }
  if (pathname === '/admin/users' || pathname.startsWith('/admin/users')) {
    return 'admin-users';
  }
  if (pathname === '/admin/settings' || pathname.startsWith('/admin/settings')) {
    return 'admin-settings';
  }
  // CB:END admin-subroutes-users-projects

  const route = getRouteBySlug(slug);
  return route ? route.screen : null;
}

/**
 * Check if a route is public (no authentication required)
 */
export function isPublicRoute(pathname: string): boolean {
  const cleanPath = pathname.split('#')[0].split('?')[0];
  const normalizedPath = cleanPath.replace(/^\/+/, '').replace(/\/+$/, '');
  const slug = normalizedPath.split('/')[0];
  if (!slug) return true; // Root path is public
  
  // CB:BEGIN invite-route-is-public
  // Invite routes are public (no auth required)
  if (slug === 'invite' && pathname.match(/^\/invite\/[^/]+/)) {
    return true;
  }
  // CB:END invite-route-is-public
  // CB:BEGIN fr-014-realtor-portal-public
  if (slug === 'realtor' && pathname.match(/^\/realtor\/portal\/[^/]+/)) {
    return true;
  }
  // CB:END fr-014-realtor-portal-public
  if (slug === 'sign-in') return true;

  const exactRoute = ROUTES.find((route) => !route.slug.includes(':') && route.slug === normalizedPath);
  if (exactRoute) return exactRoute.public;
  const route = getRouteBySlug(slug);
  return route ? route.public : true;
}
